import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Send, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/Brand";
import { PlanCards, SprintCard, CycleToggle, inr } from "@/components/PlanCards";
import { useAuth, can } from "@/context/AuthContext";
import { fmtDate } from "@/constants/options";

const loadRzp = () => new Promise((res, rej) => {
  if (window.Razorpay) { res(); return; }
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = res;
  s.onerror = () => rej(new Error("Could not load Razorpay checkout"));
  document.body.appendChild(s);
});

const Meter = ({ label, used, limit, testid }) => {
  const pct = limit ? Math.min(100, Math.round((100 * used) / limit)) : 0;
  return (
    <div data-testid={testid}>
      <div className="flex justify-between text-xs"><span className="text-[#5A6B87]">{label}</span><b>{used} / {limit ?? "∞"}</b></div>
      <div className="h-2 rounded-full bg-slate-100 mt-1"><div className={`h-full rounded-full ${pct >= 90 ? "bg-[#D64545]" : "bg-[#2E9B65]"}`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
};

const TRIAL_LABELS = { clause_updates: "Clause updates", documents: "Documents created", ai: "AI generations", exports: "DOCX/PDF exports", approvals: "Review/approval steps", evidence: "Evidence uploads", qms_records: "Quality-module records", editors: "Editor accounts" };

const TrialUsage = ({ e }) => (
  <div className="qt-card p-5 mb-6" data-testid="trial-usage-card">
    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3"><h3 className="font-display font-semibold">Free trial usage</h3>
      <span className="text-xs text-[#5A6B87]">Every feature is unlocked with a small allowance. Subscribe to remove the limits.</span></div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {Object.keys(e.trial_limits).map((k) => <Meter key={k} label={TRIAL_LABELS[k]} used={k === "editors" ? e.editors_used : e.trial_usage[k]} limit={e.trial_limits[k]} testid={`trial-meter-${k}`} />)}
    </div>
  </div>
);

function StatusCard({ e, onSync, onCancel, busy, admin }) {
  const label = { active: "Active", trial: "Free trial", expired: "Expired" }[e.state];
  return (
    <div className="qt-card p-5 grid md:grid-cols-[1.2fr_1fr_auto] gap-5 items-center mb-6" data-testid="billing-status-card">
      <div>
        <div className="qt-eyebrow">Current plan</div>
        <div className="font-display text-2xl font-bold text-[#172238]" data-testid="billing-plan-name">{e.plan_name || "No active plan"} <span className={`text-xs align-middle px-2 py-0.5 rounded-full ${e.state === "expired" ? "bg-red-50 text-[#D64545]" : "bg-[#E7F6EE] text-[#2E9B65]"}`} data-testid="billing-state">{label}</span></div>
        <div className="text-sm text-[#5A6B87] mt-1">
          {e.state === "trial" && <>Limited usage of every feature — choose a plan below to unlock full access.</>}
          {e.state === "active" && <>{e.cycle === "monthly" ? "Monthly" : "Annual"} · {e.cycle === "monthly" && e.sub_status === "active" ? "renews" : "valid until"} {fmtDate(e.current_end)}{e.sub_status === "cancel_scheduled" ? " · auto-renew cancelled" : ""}</>}
          {e.state === "expired" && <>Your data is safe and viewable. Choose a plan to continue making changes.</>}
          {e.sprint_active && <> · Launch Sprint active until {fmtDate(e.sprint.ends)}</>}
        </div>
      </div>
      <div className="space-y-3"><Meter label={e.state === "trial" ? "AI generations (trial allowance)" : "AI credits this month"} used={e.ai_used} limit={e.ai_credits} testid="meter-ai" /><Meter label="Editor accounts" used={e.editors_used} limit={e.editors_limit} testid="meter-editors" /></div>
      <div className="flex md:flex-col gap-2">
        {e.subscription_id && <Button size="sm" variant="outline" disabled={busy} onClick={onSync} data-testid="billing-sync-btn"><RefreshCw className="h-4 w-4 mr-1" />Refresh status</Button>}
        {admin && e.subscription_id && e.sub_status === "active" && <Button size="sm" variant="ghost" className="text-[#D64545]" disabled={busy} onClick={onCancel} data-testid="billing-cancel-btn">Cancel auto-renew</Button>}
      </div>
    </div>
  );
}

function ConfirmPay({ sel, rate, onClose, onPay, busy }) {
  if (!sel) return null;
  const gst = Math.round(sel.base * rate) / 100;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="pay-confirm-dialog">
        <DialogHeader><DialogTitle>{sel.title}</DialogTitle><DialogDescription>{sel.note}</DialogDescription></DialogHeader>
        <div className="text-sm space-y-1.5 border rounded-lg p-3">
          <div className="flex justify-between"><span>Price</span><span data-testid="pay-base">{inr(sel.base)}</span></div>
          <div className="flex justify-between"><span>GST ({rate}%)</span><span data-testid="pay-gst">{inr(gst)}</span></div>
          <div className="flex justify-between font-semibold border-t pt-1.5"><span>Total payable</span><span data-testid="pay-total">{inr(sel.base + gst)}</span></div>
        </div>
        <DialogFooter><Button disabled={busy} onClick={onPay} className="bg-[#48217A]" data-testid="pay-now-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CreditCard className="h-4 w-4 mr-1" />}Pay with Razorpay</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Enquiry({ item, onClose }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setMsg(""); }, [item]);
  const send = async () => { setBusy(true); try { await api.post("/billing/enquiry", { item, message: msg }); toast.success("Request sent. Nirogyam will contact you shortly"); onClose(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="enquiry-dialog">
        <DialogHeader><DialogTitle>Request: {item}</DialogTitle><DialogDescription>We'll reply by email with a quote that fits your laboratory.</DialogDescription></DialogHeader>
        <Textarea rows={4} placeholder="Optional: sites, users, timelines…" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="enquiry-message" />
        <DialogFooter><Button disabled={busy} onClick={send} className="bg-[#48217A]" data-testid="enquiry-send-btn"><Send className="h-4 w-4 mr-1" />Send request</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const History = ({ rows }) => (
  <div className="qt-card overflow-x-auto"><table className="w-full text-[13px]" data-testid="payment-history">
    <thead className="bg-[#F4F6F8] text-[11px] uppercase text-[#5A6B87]"><tr>{["Date", "Item", "Price", "GST", "Total", "Payment ID"].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr></thead>
    <tbody>{rows.length === 0 ? <tr><td colSpan={6} className="px-3 py-6 text-center text-[#5A6B87]">No payments yet.</td></tr> : rows.map((p) => (
      <tr key={p.id} className="border-t"><td className="px-3 py-2">{fmtDate(p.paid_at || p.at)}</td><td className="px-3 py-2">{p.description}</td><td className="px-3 py-2">{inr(p.base)}</td><td className="px-3 py-2">{inr(p.gst)}</td><td className="px-3 py-2 font-semibold">{inr(p.total)}</td><td className="px-3 py-2 font-mono text-xs">{p.rp_payment_id}</td></tr>))}</tbody>
  </table></div>
);

export default function Billing() {
  const { user, reload } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [data, setData] = useState(null);
  const [cycle, setCycle] = useState("annual");
  const [sel, setSel] = useState(null);
  const [enq, setEnq] = useState(null);
  const [busy, setBusy] = useState(false);
  const admin = can(user, "manage_users");
  const load = () => api.get("/billing").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { api.get("/pricing").then((r) => setPricing(r.data)); load(); }, []);
  if (!pricing || !data) return <Loader2 className="h-6 w-6 animate-spin mx-auto text-[#48217A]" />;
  const e = data.entitlement;
  const act = (fn) => async () => { setBusy(true); try { setData({ ...data, entitlement: (await fn()).data }); reload(); } catch (x) { toast.error(errMsg(x)); } finally { setBusy(false); } };
  const pickPlan = (id, p) => {
    if (!p.checkout) { setEnq(`${p.name} plan`); return; }
    if (!admin) { toast.info("Only the laboratory administrator can change the plan"); return; }
    setSel({ item: "plan", plan: id, cycle, base: p[cycle], title: `isoCred™ ${p.name} — ${cycle === "annual" ? "annual (paid upfront)" : "monthly (auto-renews)"}`,
      note: cycle === "monthly" ? "Razorpay will charge this amount every month until you cancel." : "One payment for 12 months of access." });
  };
  const pay = async () => {
    setBusy(true);
    try {
      await loadRzp();
      const { data: o } = await api.post("/billing/checkout", { item: sel.item, plan: sel.plan, cycle: sel.cycle });
      const opts = { key: o.key_id, name: o.name, description: o.description, prefill: o.prefill, notes: { lab: o.lab_name }, theme: { color: "#48217A" },
        handler: async (r) => { try { await api.post("/billing/verify", r); toast.success("Payment received. Your plan is active"); load(); reload(); } catch (x) { toast.error(errMsg(x)); } },
        modal: { ondismiss: () => setBusy(false) } };
      Object.assign(opts, o.subscription_id ? { subscription_id: o.subscription_id } : { order_id: o.order_id, amount: o.amount, currency: o.currency });
      setSel(null);
      new window.Razorpay(opts).open();
    } catch (x) { toast.error(x.response ? errMsg(x) : x.message); } finally { setBusy(false); }
  };
  return (
    <div data-testid="billing-page">
      <PageHeader eyebrow="Workspace" title="Billing & Plans" desc="Feature-based plans priced per laboratory site. Taxes are shown separately at checkout." />
      {!data.payments_enabled && <div className="qt-card p-3 mb-4 text-sm text-[#8A5A00] bg-amber-50 border-amber-200" data-testid="payments-disabled-note">Online payments are being set up. You can still send enquiries.</div>}
      <StatusCard e={e} busy={busy} admin={admin} onSync={act(() => api.post("/billing/sync"))} onCancel={act(() => api.post("/billing/cancel"))} />
      {e.state === "trial" && <TrialUsage e={e} />}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2"><h2 className="font-display text-lg font-semibold">Subscription plans</h2><CycleToggle cycle={cycle} setCycle={setCycle} /></div>
      <PlanCards pricing={pricing} cycle={cycle} current={e.state === "active" && e.cycle === cycle ? e.plan : null} onSelect={pickPlan} busy={busy} />
      <h2 className="font-display text-lg font-semibold mt-8 mb-3">Get accreditation-ready faster</h2>
      <SprintCard s={pricing.launch_sprint} busy={busy} owned={e.sprint_active}
        onBuy={() => admin ? setSel({ item: "sprint", base: pricing.launch_sprint.price, title: pricing.launch_sprint.name, note: "One-time onboarding and readiness service for 30 days." }) : toast.info("Only the laboratory administrator can purchase")} />
      <h2 className="font-display text-lg font-semibold mt-8 mb-3">Optional add-ons</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">{pricing.addons.map((a) => (
        <div key={a.id} className="qt-card p-4 flex flex-col" data-testid={`addon-${a.id}`}><div className="font-medium">{a.name}</div><div className="text-sm text-[#48217A] font-semibold mt-1 flex-1">{a.price}</div>
          <Button size="sm" variant="outline" className="mt-3 w-fit" onClick={() => setEnq(a.name)} data-testid={`addon-request-${a.id}`}>Request</Button></div>))}</div>
      <h2 className="font-display text-lg font-semibold mb-3">Payment history</h2>
      <History rows={data.payments} />
      <ConfirmPay sel={sel} rate={pricing.gst_rate} busy={busy} onClose={() => setSel(null)} onPay={pay} />
      <Enquiry item={enq} onClose={() => setEnq(null)} />
    </div>
  );
}
